import { useTranslation } from "react-i18next";

const SectionLabel = ({ children, inverted }) => (
  // The class bumps the size on mobile - see .prompter-section-label in Custom.css.
  <p className="prompter-section-label" style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: inverted ? "#038edc" : "#5fd0f3", marginBottom: 16 }}>
    {children}
  </p>
);

const SettingRow = ({ label, value, unit, children, inverted }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: inverted ? "#374151" : "rgba(255,255,255,0.75)", fontFamily: "Montserrat, sans-serif" }}>
        {label}
      </label>
      {value !== undefined && (
        <span style={{ fontSize: 12, fontWeight: 700, color: "#038edc", background: "rgba(3,142,220,0.12)", padding: "2px 10px", borderRadius: 20 }}>
          {value}{unit}
        </span>
      )}
    </div>
    {children}
  </div>
);

const ToggleRow = ({ label, checked, onChange, id, inverted }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${inverted ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.06)"}` }}>
    <label htmlFor={id} style={{ fontSize: 13, fontWeight: 500, color: inverted ? "#374151" : "rgba(255,255,255,0.75)", fontFamily: "Montserrat, sans-serif", cursor: "pointer" }}>
      {label}
    </label>
    <div
      onClick={() => onChange({ target: { checked: !checked } })}
      style={{
        width: 42, height: 24, borderRadius: 12, position: "relative", cursor: "pointer",
        background: checked ? "#038edc" : "rgba(255,255,255,0.12)",
        transition: "background 0.2s ease", flexShrink: 0,
      }}
    >
      <div style={{
        position: "absolute", top: 3, left: checked ? 21 : 3,
        width: 18, height: 18, borderRadius: "50%", background: "#fff",
        transition: "left 0.2s ease", boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
      }} />
    </div>
  </div>
);

const PresentationSettings = ({
  sentenceDuration, setSentenceDuration,
  fontSize, setFontSize,
  mirrorHorizontal, setMirrorHorizontal,
  countdown, setCountdown,
  startMode, setStartMode,
  marginTop, setMarginTop,
  marginBottom, setMarginBottom,
  showNextSentence, setShowNextSentence,
  inverted,
}) => {
  const { t } = useTranslation();

  const chipBg = inverted ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)";
  const chipColor = inverted ? "#374151" : "rgba(255,255,255,0.85)";

  const startModeOptions = [
    { value: "countdown", label: "Countdown only" },
    { value: "countdownAndStartbutton", label: "Countdown & Startbutton" },
    { value: "startbutton", label: "Startbutton only" },
  ];

  return (
    <div>
      {/* ── Start Mode (own section, full width) ── */}
      <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: `1px solid ${inverted ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.07)"}` }}>
        <SectionLabel inverted={inverted}>Start Mode</SectionLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {startModeOptions.map((opt) => (
            <button key={opt.value} onClick={() => setStartMode(opt.value)} style={{
              flex: "1 1 180px", padding: "10px 12px", borderRadius: 8, border: "none", cursor: "pointer",
              fontWeight: 700, fontSize: 13, fontFamily: "Montserrat, sans-serif",
              background: startMode === opt.value ? "#038edc" : chipBg,
              color: startMode === opt.value ? "#fff" : chipColor,
              transition: "background 0.15s ease",
            }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

    <div className="prompter-settings-grid">

      {/* ── Text column ── */}
      <div>
        <SectionLabel inverted={inverted}>{t("Backend.Post.Teleprompter.Headers.Text")}</SectionLabel>

        <SettingRow
          label={t("Backend.Post.Presentation.SentenceDuration", "Seconds per sentence")}
          value={sentenceDuration}
          unit="s"
          inverted={inverted}
        >
          <input
            type="range"
            style={{ width: "100%", accentColor: "#038edc", cursor: "pointer" }}
            min={1} max={30} step={1} value={sentenceDuration}
            onChange={(e) => setSentenceDuration(Number(e.target.value))}
          />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: inverted ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.35)", marginTop: 4 }}>
            <span>1s</span>
            <span>30s</span>
          </div>
        </SettingRow>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: inverted ? "#374151" : "rgba(255,255,255,0.75)", marginBottom: 8, display: "block", fontFamily: "Montserrat, sans-serif" }}>
            {t("Backend.Post.Teleprompter.Countdown")}
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            {[5, 10, 15].map((v) => (
              <button key={v} onClick={() => setCountdown(v)} style={{
                flex: 1, padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer",
                fontWeight: 700, fontSize: 13, fontFamily: "Montserrat, sans-serif",
                background: countdown === v ? "#038edc" : chipBg,
                color: countdown === v ? "#fff" : chipColor,
                transition: "background 0.15s ease",
              }}>
                {v}s
              </button>
            ))}
          </div>
        </div>

        <ToggleRow
          id="presentationShowNextSentenceSwitch"
          label={t("Backend.Post.Presentation.ShowNextSentence", "Preview next sentence")}
          checked={showNextSentence}
          onChange={(e) => setShowNextSentence(e.target.checked)}
          inverted={inverted}
        />

        <ToggleRow
          id="presentationMirrorHorizontalSwitch"
          label={t("Backend.Post.Teleprompter.MirrorHorizontal")}
          checked={mirrorHorizontal}
          onChange={(e) => setMirrorHorizontal(e.target.checked)}
          inverted={inverted}
        />
      </div>

      {/* ── Layout column ── */}
      <div>
        <SectionLabel inverted={inverted}>{t("Backend.Post.Teleprompter.Headers.Layout")}</SectionLabel>

        <SettingRow label={t("Backend.Post.Teleprompter.FontSize")} value={fontSize} unit="px" inverted={inverted}>
          <input
            type="range"
            style={{ width: "100%", accentColor: "#038edc", cursor: "pointer" }}
            min={10} max={100} step={1} value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
          />
        </SettingRow>

        <SettingRow label={t("Backend.Post.Teleprompter.ChangeMarginTop")} value="" unit="" inverted={inverted}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[0, 10, 50, 100, 200].map((v) => (
              <button key={v} onClick={() => setMarginTop(v)} style={{
                padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                fontWeight: 600, fontSize: 12, fontFamily: "Montserrat, sans-serif",
                background: marginTop === v ? "#038edc" : chipBg,
                color: marginTop === v ? "#fff" : chipColor,
                transition: "background 0.15s ease",
              }}>{v}</button>
            ))}
          </div>
        </SettingRow>

        <SettingRow label={t("Backend.Post.Teleprompter.ChangeMarginBottom")} value="" unit="" inverted={inverted}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[10, 50, 75, 100, 200].map((v) => (
              <button key={v} onClick={() => setMarginBottom(v)} style={{
                padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                fontWeight: 600, fontSize: 12, fontFamily: "Montserrat, sans-serif",
                background: marginBottom === v ? "#038edc" : chipBg,
                color: marginBottom === v ? "#fff" : chipColor,
                transition: "background 0.15s ease",
              }}>{v}</button>
            ))}
          </div>
        </SettingRow>
      </div>
    </div>
    </div>
  );
};

export default PresentationSettings;
